/**
 * AccessDenied Component
 * Displayed when user doesn't have permission to access a page or feature
 * Enhanced with contextual messages based on permission levels
 */

import React from 'react';
import { ShieldOff, Lock, AlertCircle, Eye, Edit3, PlusCircle } from 'lucide-react';
import { PermissionLevel } from '../types/permissions';

interface AccessDeniedProps {
  message?: string;
  suggestion?: string;
  icon?: 'shield' | 'lock' | 'alert';
  requiredLevel?: PermissionLevel;
  currentLevel?: PermissionLevel;
  pageName?: string;
}

const AccessDenied: React.FC<AccessDeniedProps> = ({ 
  message,
  suggestion,
  icon = 'shield',
  requiredLevel,
  currentLevel,
  pageName
}) => {
  const IconComponent = icon === 'lock' ? Lock : icon === 'alert' ? AlertCircle : ShieldOff;

  // Generate contextual message based on permission levels
  const getContextualMessage = () => {
    if (message) return message;
    
    if (requiredLevel && currentLevel) {
      if (currentLevel === 'none') {
        return `No Access to ${pageName || 'this page'}`;
      }
      if (currentLevel === 'read' && requiredLevel !== 'read') {
        return `Read-Only Access to ${pageName || 'this page'}`;
      }
      if (currentLevel === 'write' && requiredLevel === 'create') {
        return `Limited Access to ${pageName || 'this page'}`;
      }
    }
    
    return "You don't have permission to access this page";
  };

  const getContextualSuggestion = () => {
    if (suggestion) return suggestion;
    
    if (requiredLevel && currentLevel) {
      if (currentLevel === 'none') {
        return 'You don\'t have access to this page. Contact your administrator to request access.';
      }
      if (currentLevel === 'read') {
        if (requiredLevel === 'write') {
          return 'You have read-only access. You can view this page but cannot make changes.';
        }
        if (requiredLevel === 'create') {
          return 'You have read-only access. You can view this page but cannot create new items.';
        }
      }
      if (currentLevel === 'write' && requiredLevel === 'create') {
        return 'You can edit existing items but cannot create new ones.';
      }
    }
    
    return "Contact your administrator if you believe you should have access to this feature";
  };

  const getLevelIcon = () => {
    if (currentLevel === 'read') return <Eye className="w-5 h-5 text-accent" />;
    if (currentLevel === 'write') return <Edit3 className="w-5 h-5 text-warning" />;
    if (currentLevel === 'create') return <PlusCircle className="w-5 h-5 text-success" />;
    return null;
  };

  return (
    <div className="flex items-center justify-center min-h-[400px] p-8">
      <div className="text-center max-w-md">
        <div className="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full bg-danger-soft">
          <IconComponent className="w-10 h-10 text-danger" />
        </div>
        
        <h2 className="text-2xl font-bold text-txt mb-3">
          Access Denied
        </h2>
        
        <p className="text-txt-secondary mb-4">
          {getContextualMessage()}
        </p>
        
        <div className="bg-warning-soft border border-warning rounded-lg p-4 mb-4">
          <p className="text-sm text-warning">
            {getContextualSuggestion()}
          </p>
        </div>
        
        {currentLevel && currentLevel !== 'none' && (
          <div className="mt-6 pt-4 border-t border-default">
            <div className="flex items-center justify-center space-x-2 text-sm text-txt-secondary">
              {getLevelIcon()}
              <span>Current permission: <strong className="text-txt capitalize">{currentLevel}</strong></span>
            </div>
          </div>
        )}
        
        <div className="mt-6">
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-bg-surface hover:bg-bg-surface-hover text-txt-secondary rounded-lg transition-colors text-sm font-medium"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccessDenied;

